/* @flow */
import _ from 'lodash';
import { filter } from 'fuzzaldrin';
import { OrderedMap } from 'immutable';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';

import Chat from 'entities/Chat';
import Chats from 'collections/Chats';
import PotentialChatMember from 'entities/PotentialChatMember';
import PotentialChatMembers from 'collections/PotentialChatMembers';
import { TARGET_TYPES } from 'entities/Message';
import { search, groupChatsQuery } from 'clients/api/roleBasedMessagingSearch';

import RecipientCountQuery from '../queries/RecipientCount.gql';
import type { QueryCountRecipients } from '../queries/__generated__/QueryCountRecipients';

import type { ComposerOrgs_me_organizations } from '../chat/queries/__generated__/ComposerOrgs';
import type { ComposerGroups_me_classes } from '../chat/queries/__generated__/ComposerGroups';
import type { Target } from 'entities/Message';

const fuzzaldrinOptions = {
  key: 'searchString',
};

export type ComposerOrganization = ComposerOrgs_me_organizations & {
  studentIdPercentage: number,
};

export type ComposerGroup = ComposerGroups_me_classes;

export const RECIPIENT_TYPES = {
  STUDENT_ID_LIST_RECIPIENT: 'student_id_list_recipient',
  ORGANIZATION_RECIPIENT: 'organization_recipient',
  GROUP_RECIPIENT: 'group_recipient',
  GROUP_CHAT_RECIPIENT: 'group_chat_recipient',
  INDIVIDUAL_RECIPIENT: 'individual_recipient',
};

export type MessageFilterType = {
  type: string,
  value: {
    label: string,
    id: string,
  },
};

export type OrganizationRecipient = {
  type: 'organization_recipient',
  organization: ComposerOrganization,
  id: Backbone$Id,
  searchString?: string,
  filters: MessageFilterType[],
  selectedFilters: MessageFilterType[],
};

export type GroupRecipient = {
  type: 'group_recipient',
  group: ComposerGroup,
  id: Backbone$Id,
  searchString?: string,
  filters: MessageFilterType[],
  selectedFilters: MessageFilterType[],
};

export type GroupChatRecipient = {
  type: 'group_chat_recipient',
  chat: Chat,
  id: Backbone$Id,
  searchString?: string,
};

export type IndividualRecipient = {
  type: 'individual_recipient',
  member: PotentialChatMember,
  id: Backbone$Id,
  uuid: string,
  searchString?: string,
};

export type StudentIdListRecipient = {
  type: 'student_id_list_recipient',
  id: string,
  csvName: string,
  fileUuid: string,
  includeParents: boolean,
  includeStudents: boolean,
  orgUuid: string,
  students: *[],
};

export type Recipient =
  | StudentIdListRecipient
  | OrganizationRecipient
  | GroupRecipient
  | GroupChatRecipient
  | IndividualRecipient;

export type RecipientCount = { id: string, count: number };
export type RecipientCountsMap = OrderedMap<
  $PropertyType<RecipientCount, 'id'>,
  $PropertyType<RecipientCount, 'count'>
>;

export type State = {
  recipients: Recipient[], // flat list of all recipients
  results: Array<Recipient[]>, // recipients grouped by class
  resultSelectionIndex: number, // which of a class' results is selected
  selection: Recipient[], // flat list of chosen recipients
  targets: Target[],
  query: string,
  highlightIndex: number,
  message: string,
  loadingIndividualRecipients?: boolean,
  loadingGroupChats?: boolean,
  districtIsSelected?: boolean,
  totalRecipientCount: number, // only a reflection of the recipientCountsMap
  recipientCountsMap: RecipientCountsMap,
};

const initialState: State = {
  recipients: [],
  results: [],
  resultSelectionIndex: 0,
  selection: [],
  targets: [],
  query: '',
  highlightIndex: -1,
  message: '',
  totalRecipientCount: 0,
  recipientCountsMap: new OrderedMap(),
};

export const createTargets = (recipients: Recipient[]): Target[] => {
  return recipients.map((r: Recipient) => {
    let t;
    if (r.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT) {
      t = {
        type: TARGET_TYPES.ORGANIZATION_TARGET,
        org_id: r.organization.id,
        uuid: r.organization.uuid,
        selectedFilters: r.selectedFilters,
        filters: r.filters,
      };
    } else if (r.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
      t = {
        type: TARGET_TYPES.GROUP_TARGET,
        group_id: r.group.id,
        uuid: r.group.uuid,
        selectedFilters: r.selectedFilters,
        filters: r.filters,
      };
    } else if (r.type === RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT) {
      t = {
        type: TARGET_TYPES.GROUP_CHAT_TARGET,
        chat_id: r.chat.id,
        uuid: r.chat.id,
      };
    } else if (r.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT) {
      t = {
        type: TARGET_TYPES.INDIVIDUAL_TARGET,
        member_id: r.member.id,
        uuid: r.member.getUuid(),
      };
    } else if (r.type === RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT) {
      t = {
        type: TARGET_TYPES.STUDENT_ID_LIST_TARGET,
        uuid: r.id,
      };
    }
    // $FlowFixMe possibly uninitialized
    return t;
  });
};

const generateResults = recipients => {
  const org = recipients.filter(r => r.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT);
  // $FlowIssue Flow can't refine types in filter https://github.com/facebook/flow/issues/1414
  const group: GroupRecipient[] = recipients.filter(
    r => r.type === RECIPIENT_TYPES.GROUP_RECIPIENT
  );
  const groupChat = recipients.filter(r => r.type === RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT);
  // We want to surface newly created group chats.
  const orderedGroupChats = _.orderBy(groupChat, ['chat.attributes.sort_key'], ['desc']);
  const individual = recipients.filter(r => r.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT);
  const orgResults = _.values(_.groupBy([...org], r => r.id));
  const groupResults = _.values(_.groupBy([...group], r => r.id));
  const groupChatResults = _.values(_.groupBy([...orderedGroupChats], r => r.id));
  const individualResults = _.values(_.groupBy([...individual], r => r.id));
  return [...orgResults, ...groupResults, ...groupChatResults, ...individualResults];
};

const calcResults = (recipients, query): Recipient[] => {
  if (query) {
    return filter(recipients, query, fuzzaldrinOptions);
  } else {
    return recipients;
  }
};

const createIndividualRecipient = (member: PotentialChatMember): IndividualRecipient => {
  return {
    type: 'individual_recipient',
    member,
    id: member.id.toString(),
    uuid: member.getUuid(),
    searchString: `${member.getName()} ${member.getFirstName()} ${member.getLastName()} ${member.getNote()} ${member.getGroupNames()}`,
  };
};

const createOrgRecipient = (organization: ComposerOrganization): OrganizationRecipient => {
  const { messageTargetFilters } = organization;
  const filters = messageTargetFilters
    ? _.flatMap(messageTargetFilters, messageTargetFilter => {
        return messageTargetFilter.values.map(value => {
          return {
            type: messageTargetFilter.id,
            value,
          };
        });
      })
    : [];
  // eslint-disable-line
  return {
    type: RECIPIENT_TYPES.ORGANIZATION_RECIPIENT,
    organization,
    id: organization.id,
    searchString: `${organization.name}`,
    selectedFilters: [],
    filters,
  };
};

const createGroupRecipient = (group: ComposerGroup): GroupRecipient => {
  const { messageTargetFilters } = group;
  const filters = messageTargetFilters
    ? _.flatMap(messageTargetFilters, messageTargetFilter => {
        return messageTargetFilter.values.map(value => {
          return {
            type: messageTargetFilter.id,
            value,
          };
        });
      })
    : [];
  // eslint-disable-line
  return {
    type: RECIPIENT_TYPES.GROUP_RECIPIENT,
    group,
    id: group.id,
    searchString: `${group.className}`,
    selectedFilters: [],
    filters,
  };
};

export const createGroupChatRecipient = (chat: Chat): GroupChatRecipient => {
  return {
    type: 'group_chat_recipient',
    chat,
    id: chat.id.toString(),
    searchString: `${chat.getTitle()}`,
  };
};

function selectionContainsDistrict(selection: Recipient[]) {
  return selection.some(s => s.type === 'organization_recipient');
}

function removeRecipientFromSelection(selection: Recipient[], recipient: Recipient) {
  return selection.filter(selectedRecipient => selectedRecipient.id !== recipient.id);
}

function addRecipientToSelection(selection: Recipient[], recipient: Recipient) {
  const removed = removeRecipientFromSelection(selection, recipient);
  removed.push(recipient);
  return removed;
}

const getTotalCountFromMap = (map: RecipientCountsMap) => {
  return map.reduce((sum, n) => sum + n) || 0;
};

// used to remove any map elements that are not also in the list
// returns a new immutable data type
const removeMapItemsNotInList = (map: OrderedMap<*, *>, list: Array<*>) => {
  return map.filter((val, kId) => {
    return list.find(r => {
      return `${r.id}` === `${kId}`;
    });
  });
};

type loadOrganizationsAction = {
  type: 'rbms/LOAD_ORGANIZATIONS',
  organizations: ComposerOrganization[],
};

export const loadOrganizations = (
  organizations: ComposerOrganization[]
): loadOrganizationsAction => ({
  type: 'rbms/LOAD_ORGANIZATIONS',
  organizations,
});

type loadGroupsAction = {
  type: 'rbms/LOAD_GROUPS',
  groups: ComposerGroup[],
};

export const loadGroups = (groups: ComposerGroup[]): loadGroupsAction => ({
  type: 'rbms/LOAD_GROUPS',
  groups,
});

type loadGroupChatsAction = {
  type: 'rbms/LOAD_GROUP_CHATS',
  groupChats: Chats,
};

export const loadGroupChats = (groupChats: Chats): loadGroupChatsAction => ({
  type: 'rbms/LOAD_GROUP_CHATS',
  groupChats,
});

type loadIndividualsAction = {
  type: 'rbms/LOAD_INDIVIDUALS',
  members: PotentialChatMember[],
};

export const loadIndividuals = (members: PotentialChatMember[]): loadIndividualsAction => ({
  type: 'rbms/LOAD_INDIVIDUALS',
  members,
});

type changeQueryAction = {
  type: 'rbms/CHANGE_QUERY',
  query: string,
};

export const changeQuery = (query: string): changeQueryAction => ({
  type: 'rbms/CHANGE_QUERY',
  query,
});

type saveMessageAction = {
  type: 'rbms/SAVE_MESSAGE',
  message: string,
};

export const saveMessage = (message: string): saveMessageAction => ({
  type: 'rbms/SAVE_MESSAGE',
  message,
});

type selectStudentIdListRecipientAction = {
  type: 'rbms/SELECT_STUDENT_ID_LIST_RECIPIENT',
  recipient: StudentIdListRecipient,
};

export const selectStudentIdListRecipient = (
  recipient: StudentIdListRecipient
): selectStudentIdListRecipientAction => ({
  type: 'rbms/SELECT_STUDENT_ID_LIST_RECIPIENT',
  recipient,
});

type selectRecipientAction = {
  type: 'rbms/SELECT_RECIPIENT',
  recipient: Recipient,
  newFilter: ?MessageFilterType,
};

export const internalSelectRecipient = (
  recipient: Recipient,
  newFilter: ?MessageFilterType
): selectRecipientAction => ({
  type: 'rbms/SELECT_RECIPIENT',
  recipient,
  newFilter,
});

type selectionCountSuccessAction = {
  type: 'rbms/SELECTION_COUNT_SUCCESS',
  count: number,
  id: string,
};

export const selectionCountSuccess = (count: number, id: string): selectionCountSuccessAction => ({
  type: 'rbms/SELECTION_COUNT_SUCCESS',
  count,
  id,
});

const getRecipientType = (recipient: Recipient): ?string => {
  if (recipient.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT) {
    return 'organization';
  } else if (recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
    return 'group';
  } else {
    return null;
  }
};

const getRecipientUUID = (recipient: Recipient): string => {
  if (recipient.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT) {
    return recipient.organization.uuid;
  } else if (recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
    return recipient.group.uuid;
  } else {
    return '';
  }
};

// Promise<QueryCountRecipients> | Promise<number> | Promise<void>
const getRecipientCountFromRecipient = async (
  recipient: Recipient
): Promise<?QueryCountRecipients> => {
  const makeQueryCountRecipient = (num: number): QueryCountRecipients => {
    return {
      countRecipients: {
        __typename: 'RecipientsQueryResult',
        count: num,
      },
    };
  };

  if (recipient.type === RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT) {
    return makeQueryCountRecipient(recipient.chat.getMembershipsCount() - 1);
  }

  if (recipient.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT) {
    return makeQueryCountRecipient(1);
  }

  if (recipient.type === RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT) {
    return null;
  }

  try {
    const type = getRecipientType(recipient);
    const uuid = getRecipientUUID(recipient);
    const roles = recipient.selectedFilters
      ? recipient.selectedFilters
          .filter(filtered => filtered.type === 'roles')
          .map(filtered => filtered.value.id)
      : [];
    const filters = roles.length ? [{ roles }] : [];
    const gqlData = await getApolloClientSingleton().query({
      query: RecipientCountQuery,
      skip:
        recipient.type !== RECIPIENT_TYPES.ORGANIZATION_RECIPIENT &&
        recipient.type !== RECIPIENT_TYPES.GROUP_RECIPIENT,
      variables: {
        input: {
          type,
          uuid,
          filters,
        },
      },
    });
    const { data }: { data: QueryCountRecipients } = gqlData;
    return data;
  } catch (err) {
    // propagate up so we can have a more meaningful error message
    throw err;
  }
};

export const getRecipientCountsFromList = async (
  recipients: $ReadOnlyArray<Recipient>
): Promise<Array<?QueryCountRecipients>> => {
  return Promise.all(recipients.map(recipient => getRecipientCountFromRecipient(recipient)));
};

export const selectRecipient = (recipient: Recipient, newFilter?: MessageFilterType) => {
  return async (dispatch: Function, getState: () => { roleBasedMessagingSearch: State }) => {
    await dispatch(internalSelectRecipient(recipient, newFilter));
    const { roleBasedMessagingSearch } = getState();

    const selectedRecipient = roleBasedMessagingSearch.selection.find(
      rec => rec.id === recipient.id
    );

    try {
      if (!selectedRecipient) {
        return;
      }

      const count = await getRecipientCountFromRecipient(selectedRecipient);

      if (count) {
        dispatch(selectionCountSuccess(count.countRecipients.count, `${selectedRecipient.id}`));
      }
    } catch (e) {
      console.error('loadSelectedRecipientCount failure', e);
    }
  };
};

type selectInitialTargetsAction = {
  type: 'rbms/SELECT_INITIAL_TARGETS',
  targets: Target[],
};

export const selectInitialTargets = (targets: Target[]): selectInitialTargetsAction => ({
  type: 'rbms/SELECT_INITIAL_TARGETS',
  targets,
});

type flushAction = {
  type: 'rbms/FLUSH',
};

export const flush = (): flushAction => ({
  type: 'rbms/FLUSH',
});

type clearSelectionAction = {
  type: 'rbms/CLEAR_SELECTION',
};

export const clearSelection = (): clearSelectionAction => ({
  type: 'rbms/CLEAR_SELECTION',
});

export type loadPotentialChatMembersSuccessAction = {
  type: 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST_SUCCESS',
  members: PotentialChatMembers,
};

export const loadPotentialChatMembersSuccess = (
  members: PotentialChatMembers
): loadPotentialChatMembersSuccessAction => ({
  type: 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST_SUCCESS',
  members,
});

export type loadPotentialChatMembersRequestAction = {
  type: 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST',
  query: string,
};

export const loadPotentialChatMembersRequest = (
  query: string
): loadPotentialChatMembersRequestAction => ({
  type: 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST',
  query,
});

export const loadPotentialChatMembers = (query: string) => {
  return async (dispatch: Function) => {
    dispatch(loadPotentialChatMembersRequest(query));

    try {
      const members = await search(query);
      dispatch(loadPotentialChatMembersSuccess(members));
    } catch (e) {
      console.error('loadPotentialChatMembers failure', e);
    }
  };
};

export type loadGroupChatsSuccessAction = {
  type: 'rbms/LOAD_GROUP_CHATS_REQUEST_SUCCESS',
  groupChats: Chats,
};

export const loadGroupChatsSuccess = (groupChats: Chats): loadGroupChatsSuccessAction => ({
  type: 'rbms/LOAD_GROUP_CHATS_REQUEST_SUCCESS',
  groupChats,
});

export type loadGroupChatsRequestAction = {
  type: 'rbms/LOAD_GROUP_CHATS_REQUEST',
  query: string,
};

export const loadGroupChatsRequest = (query: string): loadGroupChatsRequestAction => ({
  type: 'rbms/LOAD_GROUP_CHATS_REQUEST',
  query,
});

export const searchGroupChats = (query: string) => {
  return async (dispatch: Function) => {
    dispatch(loadGroupChatsRequest(query));

    try {
      const chats = await groupChatsQuery(query);
      dispatch(loadGroupChatsSuccess(chats));
    } catch (e) {
      console.error('loadGroupChats failure', e);
    }
  };
};

export type RbmsAction =
  | loadOrganizationsAction
  | loadGroupsAction
  | loadGroupChatsAction
  | loadIndividualsAction
  | changeQueryAction
  | saveMessageAction
  | selectRecipientAction
  | selectStudentIdListRecipientAction
  | selectInitialTargetsAction
  | flushAction
  | clearSelectionAction
  | loadPotentialChatMembersRequestAction
  | loadPotentialChatMembersSuccessAction
  | loadGroupChatsRequestAction
  | loadGroupChatsSuccessAction;

export default (state: State = initialState, action: *): State => {
  switch (action.type) {
    case 'rbms/LOAD_ORGANIZATIONS': {
      const { organizations } = action;
      const recipients = organizations.map(o => createOrgRecipient(o));
      const { recipients: currentRecipients, ...rest } = state;
      const allRecipients = _.uniqBy([...recipients, ...currentRecipients], r => r.id);
      return {
        ...rest,
        results: generateResults(calcResults(allRecipients, state.query)),
        recipients: allRecipients,
      };
    }

    case 'rbms/LOAD_GROUPS': {
      const { groups } = action;
      const recipients = groups
        .filter(g => g.type !== 'district')
        .map(g => createGroupRecipient(g));
      const { recipients: currentRecipients, ...rest } = state;
      const allRecipients = _.uniqBy([...recipients, ...currentRecipients], r => r.id);

      return {
        ...rest,
        results: generateResults(calcResults(allRecipients, state.query)),
        recipients: allRecipients,
      };
    }

    case 'rbms/LOAD_GROUP_CHATS': {
      const { groupChats } = action;
      const recipients = groupChats.models.map(c => createGroupChatRecipient(c));
      const { recipients: currentRecipients, ...rest } = state;
      const allRecipients = _.uniqBy([...recipients, ...currentRecipients], r => r.id);
      return {
        ...rest,
        results: generateResults(calcResults(allRecipients, state.query)),
        recipients: allRecipients,
      };
    }

    case 'rbms/LOAD_INDIVIDUALS': {
      const { members } = action;
      const recipients = members.map(m => createIndividualRecipient(m));
      const { recipients: currentRecipients, ...rest } = state;
      const allRecipients = _.uniqBy([...recipients, ...currentRecipients], r => r.id);

      return {
        ...rest,
        results: generateResults(calcResults(allRecipients, state.query)),
        recipients: allRecipients,
      };
    }

    case 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST_SUCCESS': {
      const { members } = action;
      const { recipients: currentRecipients, query } = state;
      let allRecipients = [...currentRecipients];
      if (members) {
        const recipients = members.map(m => createIndividualRecipient(m));
        allRecipients = [...allRecipients, ...recipients];
      }
      allRecipients = _.uniqBy(allRecipients, r => r.id);

      return {
        ...state,
        results: generateResults(calcResults(allRecipients, query)),
        recipients: allRecipients,
        loadingIndividualRecipients: false,
        query,
      };
    }

    case 'rbms/CHANGE_QUERY': {
      const { query } = action;
      return {
        ...state,
        results: generateResults(calcResults(state.recipients, query)),
        resultSelectionIndex: 0,
        query,
      };
    }

    case 'rbms/SAVE_MESSAGE': {
      const { message } = action;
      return {
        ...state,
        message,
      };
    }

    case 'rbms/SELECT_STUDENT_ID_LIST_RECIPIENT': {
      const { recipient } = action;
      const { selection } = state;
      const recipientInSelection = selection.find(
        selectedRecipient => selectedRecipient.id === recipient.id
      );
      const selectionWithRecipientAdded = addRecipientToSelection(selection, recipient);
      const selectionWithRecipientRemoved = removeRecipientFromSelection(selection, recipient);
      const recipientSelection = recipientInSelection
        ? [...selectionWithRecipientRemoved, recipient]
        : selectionWithRecipientAdded;
      return {
        ...state,
        selection: recipientSelection,
        targets: createTargets(recipientSelection),
      };
    }

    case 'rbms/SELECT_RECIPIENT': {
      const { recipient, newFilter } = action;
      const { selection, recipientCountsMap } = state;
      const recipientInSelection = selection.find(
        selectedRecipient => selectedRecipient.id === recipient.id
      );

      if (
        recipient.type !== RECIPIENT_TYPES.ORGANIZATION_RECIPIENT &&
        recipient.type !== RECIPIENT_TYPES.GROUP_RECIPIENT
      ) {
        if (recipientInSelection) {
          const selectionWithRecipientRemoved = removeRecipientFromSelection(selection, recipient);
          const newMap = removeMapItemsNotInList(recipientCountsMap, selectionWithRecipientRemoved);
          return {
            ...state,
            districtIsSelected: selectionContainsDistrict(selectionWithRecipientRemoved),
            selection: selectionWithRecipientRemoved,
            recipientCountsMap: newMap,
            totalRecipientCount: getTotalCountFromMap(newMap),
            targets: createTargets(selectionWithRecipientRemoved),
          };
        } else {
          const selectionWithRecipientAdded = addRecipientToSelection(selection, recipient);
          const newMap = removeMapItemsNotInList(recipientCountsMap, selectionWithRecipientAdded);
          return {
            ...state,
            districtIsSelected: selectionContainsDistrict(selectionWithRecipientAdded),
            selection: selectionWithRecipientAdded,
            recipientCountsMap: newMap,
            totalRecipientCount: getTotalCountFromMap(newMap),
            targets: createTargets(selectionWithRecipientAdded),
          };
        }
      }

      let recipientSelection = [...selection];
      const existingFiltersOnRecipient =
        recipientInSelection &&
        (recipientInSelection.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT ||
          recipientInSelection.type === RECIPIENT_TYPES.GROUP_RECIPIENT)
          ? recipientInSelection.selectedFilters
          : [];
      const unfilteredRecipient = { ...recipient, selectedFilters: [] };

      if (newFilter) {
        const recipientWithNewFilterAdded = {
          ...recipient,
          selectedFilters: [...existingFiltersOnRecipient, newFilter],
        };
        const recipientWithNewFilterRemoved = {
          ...recipient,
          selectedFilters: existingFiltersOnRecipient.filter(
            existingFilter => existingFilter.value.id !== newFilter.value.id
          ),
        };
        const recipientWithNewFilterTypeRemoved = {
          ...recipient,
          selectedFilters: existingFiltersOnRecipient.filter(
            existingFilter => existingFilter.type !== newFilter.type
          ),
        };
        const newFilterAlreadyExistsOnRecipient = existingFiltersOnRecipient.some(
          existingFilter => existingFilter.value.id === newFilter.value.id
        );
        const newFilterIsTheOnlyFilterOnRecipient =
          newFilterAlreadyExistsOnRecipient && existingFiltersOnRecipient.length === 1;
        const newFilterSelectsAllOfOneType = newFilter.value.id === 'all';
        const existingFiltersAreSameTypeAsNewFilter = existingFiltersOnRecipient
          ? existingFiltersOnRecipient.some(
              existingFilter => existingFilter.type === newFilter.type
            )
          : false;

        if (newFilterSelectsAllOfOneType && recipientInSelection) {
          recipientSelection = existingFiltersAreSameTypeAsNewFilter
            ? addRecipientToSelection(selection, recipientWithNewFilterTypeRemoved)
            : [...selection];
        } else if (newFilterSelectsAllOfOneType && !recipientInSelection) {
          recipientSelection = addRecipientToSelection(selection, unfilteredRecipient);
        } else if (newFilterIsTheOnlyFilterOnRecipient) {
          recipientSelection = removeRecipientFromSelection(selection, recipient);
        } else {
          recipientSelection = newFilterAlreadyExistsOnRecipient
            ? addRecipientToSelection(selection, recipientWithNewFilterRemoved)
            : addRecipientToSelection(selection, recipientWithNewFilterAdded);
        }
      } else {
        recipientSelection = recipientInSelection
          ? removeRecipientFromSelection(selection, recipient)
          : addRecipientToSelection(selection, unfilteredRecipient);
      }

      const newMap = removeMapItemsNotInList(recipientCountsMap, recipientSelection);

      return {
        ...state,
        districtIsSelected: selectionContainsDistrict(recipientSelection),
        selection: recipientSelection,
        recipientCountsMap: newMap,
        totalRecipientCount: getTotalCountFromMap(newMap),
        targets: createTargets(recipientSelection),
      };
    }

    case 'rbms/SELECT_INITIAL_TARGETS': {
      const { targets } = action;
      const selection = targets
        .map(t => {
          return state.recipients.find(r => {
            return r.id === t.group_id || r.id === t.org_id;
          });
        })
        .filter(Boolean);

      const selectionWithTargetFilters = selection[0]
        ? [{ ...selection[0], selectedFilters: targets[0].selectedFilters || [] }]
        : selection;

      return {
        ...state,
        selection: selectionWithTargetFilters,
        districtIsSelected: selectionContainsDistrict(selectionWithTargetFilters),

        targets: createTargets(selectionWithTargetFilters),
      };
    }

    case 'rbms/FLUSH': {
      return initialState;
    }

    case 'rbms/CLEAR_SELECTION': {
      return {
        ...state,
        selection: [],
        targets: [],
        districtIsSelected: false,
        totalRecipientCount: 0,
        recipientCountsMap: state.recipientCountsMap.clear(),
      };
    }

    case 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST': {
      return {
        ...state,
        query: action.query,
        loadingIndividualRecipients: true,
      };
    }

    case 'rbms/LOAD_GROUP_CHATS_REQUEST': {
      return {
        ...state,
        query: action.query,
        loadingGroupChats: true,
      };
    }

    case 'rbms/LOAD_GROUP_CHATS_REQUEST_SUCCESS': {
      const { groupChats } = action;
      const { recipients: currentRecipients, query } = state;
      let allRecipients = [...currentRecipients];
      if (groupChats) {
        const recipients = groupChats.models.map(gc => createGroupChatRecipient(gc));
        allRecipients = [...allRecipients, ...recipients];
      }
      allRecipients = _.uniqBy(allRecipients, r => r.id);

      return {
        ...state,
        results: generateResults(calcResults(allRecipients, query)),
        recipients: allRecipients,
        loadingGroupChats: false,
        query,
      };
    }

    case 'rbms/SELECTION_COUNT_SUCCESS': {
      const { count, id } = action;
      const { recipientCountsMap } = state;
      const newMap = recipientCountsMap.set(id, count);
      return {
        ...state,
        recipientCountsMap: newMap,
        totalRecipientCount: getTotalCountFromMap(newMap),
      };
    }

    default:
      return state;
  }
};
